// src/components/ReservationProgressBar/ReservationProgressBar.jsx
import React from 'react';
import styles from './ReservationProgressBar.module.css';
import Button from '../Button/Button';

/**
 * Progress bar component showing reservation workflow steps
 * Steps: Submit Request → Sign Agreement → Make Payment → Approved
 */
const ReservationProgressBar = ({
    reservation,
    onSignAgreement,
    onMakePayment,
    isLoadingSign = false
}) => {
    const { status, agreementStatus, paymentStatus } = reservation;

    // Determine step statuses
    const steps = [
        {
            id: 'submit',
            label: 'Request Submitted',
            shortLabel: 'Submitted',
            completed: true, // Always complete
            current: false,  // It's the starting state, once it exists, focus moves forward
        },
        {
            id: 'sign',
            label: 'Sign Agreement',
            shortLabel: 'Agreement',
            completed: agreementStatus === 'signed' || agreementStatus === 'signed_manual',
            current: status === 'pending' && agreementStatus !== 'signed' && agreementStatus !== 'signed_manual',
            action: agreementStatus !== 'signed' && agreementStatus !== 'signed_manual' && status === 'pending' ? onSignAgreement : null,
            actionLabel: 'Sign Now',
        },
        {
            id: 'pay',
            label: 'Make Payment',
            shortLabel: 'Payment',
            completed: paymentStatus === 'paid' || paymentStatus === 'paid_manual',
            current: (agreementStatus === 'signed' || agreementStatus === 'signed_manual') && paymentStatus !== 'paid' && paymentStatus !== 'paid_manual' && status === 'pending',
            // Only enable payment action if agreement is signed
            action: (agreementStatus === 'signed' || agreementStatus === 'signed_manual') && paymentStatus !== 'paid' && paymentStatus !== 'paid_manual' && status === 'pending' ? onMakePayment : null,
            actionLabel: 'Pay Now',
            disabled: agreementStatus !== 'signed' && agreementStatus !== 'signed_manual', // Greyed out until signed
        },
        {
            id: 'approved',
            label: 'Approved',
            shortLabel: 'Approved',
            completed: status === 'approved',
            current: status === 'approved',
        },
    ];

    // Handle rejected/cancelled status
    if (status === 'rejected' || status === 'cancelled') {
        return (
            <div className={styles.progressContainer}>
                <div className={styles.statusBanner + ' ' + styles[status]}>
                    <span className={styles.statusIcon}>{status === 'rejected' ? '✕' : '⊘'}</span>
                    <span>Reservation {status === 'rejected' ? 'Rejected' : 'Cancelled'}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.progressContainer}>
            <div className={styles.progressTrack}>
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                        {/* Step circle and label */}
                        <div className={`${styles.step} ${step.completed ? styles.completed : ''} ${step.current && !step.completed ? styles.current : ''} ${step.disabled ? styles.disabled : ''}`}>
                            <div className={styles.stepCircle}>
                                {step.completed ? (
                                    <span className={styles.checkmark}>✓</span>
                                ) : (
                                    <span className={styles.stepNumber}>{index + 1}</span>
                                )}
                            </div>
                            <div className={styles.stepContent}>
                                <span className={styles.stepLabel}>{step.shortLabel}</span>
                                {step.action && (
                                    <Button
                                        variant={step.id === 'sign' ? 'warning' : 'primary'}
                                        size="xs"
                                        onClick={step.action}
                                        disabled={step.disabled || isLoadingSign}
                                        className={styles.stepAction}
                                    >
                                        {isLoadingSign && step.id === 'sign' ? 'Loading...' : step.actionLabel}
                                    </Button>
                                )}
                                {step.completed && !step.action && (
                                    <span className={styles.completedLabel}>Done</span>
                                )}
                            </div>
                        </div>

                        {/* Connector line between steps */}
                        {index < steps.length - 1 && (
                            <div className={`${styles.connector} ${steps[index + 1].completed || steps[index].completed ? styles.connectorActive : ''}`} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default ReservationProgressBar;
